/*
 * Purpose: Switchable design style — Surface (steercloud) vs Routines (autoblocks.ai)
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DESIGN_STYLE_STORAGE_KEY,
  type DesignStyleId,
} from "@/lib/design-tokens/routines";

type DesignStyleContextValue = {
  designStyle: DesignStyleId;
  setDesignStyle: (style: DesignStyleId) => void;
};

const DesignStyleContext = createContext<DesignStyleContextValue | null>(null);

function applyDesignStyleToDocument(style: DesignStyleId) {
  document.documentElement.setAttribute("data-design-style", style);
}

export function DesignStyleProvider({ children }: { children: ReactNode }) {
  const [designStyle, setDesignStyleState] = useState<DesignStyleId>(() => {
    if (typeof window === "undefined") return "surface";
    const stored = window.localStorage.getItem(
      DESIGN_STYLE_STORAGE_KEY,
    ) as DesignStyleId | null;
    return stored === "routines" || stored === "surface" ? stored : "surface";
  });

  useEffect(() => {
    applyDesignStyleToDocument(designStyle);
  }, [designStyle]);

  const setDesignStyle = useCallback((style: DesignStyleId) => {
    setDesignStyleState(style);
    window.localStorage.setItem(DESIGN_STYLE_STORAGE_KEY, style);
    applyDesignStyleToDocument(style);
  }, []);

  const value = useMemo(
    () => ({ designStyle, setDesignStyle }),
    [designStyle, setDesignStyle],
  );

  return (
    <DesignStyleContext.Provider value={value}>
      <div data-routines-app="">{children}</div>
    </DesignStyleContext.Provider>
  );
}

export function useDesignStyle(): DesignStyleContextValue {
  const ctx = useContext(DesignStyleContext);
  if (!ctx) {
    throw new Error("useDesignStyle must be used within DesignStyleProvider");
  }
  return ctx;
}
